import { Button, VStack, useDisclosure, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ServerError } from '@texas/types';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { DataTableContainer } from '@texas/components/shared/dataTable/DataTableContainer';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { ConfigurationPage } from '@texas/components/configuration/layout/ConfigurationPage';
import {
  DimensionGroupListItem,
  dimensionGroupsApi,
  SearchQuery,
} from '@texas/api/endpoints/metadata/dimensionGroupsApi';
import { dimensionGroupTableColumns } from './Columns';
import { ArchiveDimensionGroup } from './ArchiveDimensionGroup';
import { CreateDimensionGroup } from './CreateDimensionGroup';
import { EditDimensionGroup } from './EditDimensionGroup';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { TexasSwitch } from '@texas/components/shared/form/TexasSwitch';

export function DimensionGroups({ folderId }: { folderId: number }) {
  const {
    onOpen: onCreateOpen,
    onClose: onCreateClose,
    isOpen: isCreateOpen,
  } = useDisclosure();
  const {
    value: archiveValue,
    isOpen: isArchiveOpen,
    onOpen: onArchiveOpen,
    onClose: onArchiveClose,
  } = useValueDisclosure<DimensionGroupListItem>();
  const {
    value: editValue,
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useValueDisclosure<number>();

  const { t } = useTranslation();
  const toast = useToast();

  const {
    data: dimensionGroups,
    refetch,
    error,
    loading,
  } = useApiResource(dimensionGroupsApi.search);

  const { request: restoreDimensionGroupRequest } = useApiRequest(
    dimensionGroupsApi.restore,
  );

  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    ...{
      includeArchived: false,
      searchTerm: '',
      sortBy: '',
      sortDesc: false,
    },
    sortBy: 'name',
  });

  useEffect(() => {
    refetch(searchQuery);
  }, [refetch, searchQuery]);

  const performRestore = useCallback(
    async (id: number) => {
      await request(
        restoreDimensionGroupRequest,
        [id],
        () => {
          toast({
            title: t('general.restored'),
            status: 'success',
            isClosable: true,
          });
          refetch(searchQuery);
        },
        (error: ServerError) => {
          toast({
            title: t('general.restoreFailed'),
            description: error.message,
            status: 'error',
            isClosable: true,
          });
        },
      );
    },
    [restoreDimensionGroupRequest, toast, t, refetch, searchQuery],
  );

  const columns = useMemo(() => {
    return dimensionGroupTableColumns(
      t,
      onArchiveOpen,
      performRestore,
      onEditOpen,
    );
  }, [t, onArchiveOpen, performRestore, onEditOpen]);

  return (
    <ConfigurationPage
      title={t('configuration.dimensionGroups')}
      toolbar={
        <>
          <SearchFilterInput
            value={searchQuery.searchTerm}
            style="entityDirectory"
            onChange={(s) => setSearchQuery({ ...searchQuery, searchTerm: s })}
          />
          <TexasSwitch
            checked={searchQuery.includeArchived}
            label={t('general.includeArchived')}
            onChange={(checked) =>
              setSearchQuery({
                ...searchQuery,
                includeArchived: checked,
              })
            }
          />
        </>
      }
      action={
        <Button variant="entityDirectoryPrimary" onClick={onCreateOpen}>
          {t('general.addNew')}
        </Button>
      }
    >
      {archiveValue && (
        <ArchiveDimensionGroup
          dimensionGroup={archiveValue}
          isOpen={isArchiveOpen}
          onClose={onArchiveClose}
          onArchive={() => {
            refetch(searchQuery);
          }}
        />
      )}

      <CreateDimensionGroup
        onClose={onCreateClose}
        isOpen={isCreateOpen}
        onCreated={() => {
          refetch(searchQuery);
          onCreateClose();
        }}
        folderId={folderId}
      />

      <EditDimensionGroup
        dimensionGroupId={editValue}
        isOpen={isEditOpen}
        onClose={onEditClose}
        onUpdated={() => {
          refetch(searchQuery);
        }}
        folderId={folderId}
      />
      <VStack w="100%" alignItems="start">
        <DataTableContainer
          w="100%"
          datatable={{
            data: dimensionGroups ?? [],
            sorted: {
              onSortedChange: ({ key, desc }) => {
                setSearchQuery({ ...searchQuery, sortBy: key, sortDesc: desc });
              },
              key: searchQuery.sortBy,
              desc: searchQuery.sortDesc,
            },
            isLoading: loading,
            columns: columns,
            variant: 'entity-directory',
          }}
          error={error}
        />
      </VStack>
    </ConfigurationPage>
  );
}
